import { DisabledHint } from '../EditorHeader/translation/DisabledHint'
import { useContentMessages } from '@/features/editor/hooks/useContentMessages'
import { useState } from 'react'
import { Input, Label, ScrollArea, SegmentedControl } from '@teach/ui'
import type { LessonUpdate } from '@/types/lesson'
import { LessonLookSection } from './LessonLookSection'
import { LessonDescriptionField } from './LessonDescriptionField'
import { LessonDeleteButton } from './LessonDeleteButton'
import { useLessonTextAutosave } from './useLessonTextAutosave'
import { REVIEW_LOCK_TEXT } from '@/features/editor/lib/reviewLockText'
import { useMessages } from '@/i18n'
import { messages } from './messages'

const tabsOf = (m: { tabInfo: string; tabLook: string }) => [
  { id: 'info' as const, label: m.tabInfo },
  { id: 'look' as const, label: m.tabLook },
]

interface Props {
  courseId: number
  lessonId: number
  lessonNumber: number
  name: string
  description: string
  /** Тема урока — сырое значение бэка: он его не валидирует. */
  lessonTheme: string | null | undefined
  savePatch: (patch: LessonUpdate) => Promise<boolean>
  isCourseAuthor: boolean
  /** Идёт прогон перевода (§7.5): он взял снимок текста курса на старте, и
   *  правка названия/описания по ходу молча исчезла бы при записи патча. */
  isTextLocked: boolean
  /** Курс на проверке (`useIsCourseFrozen`) — та же блокировка текста, но
   *  подпись обязана назвать проверку, а не перевод; вдобавок гаснут картинка
   *  и оформление урока. */
  isFrozen: boolean
  onSave: (name: string, description: string) => void
  onDelete: () => void
}

/**
 * Right-panel lesson editor (ref LessonInfoPanel): shown when a lesson — not a
 * block — is selected. Edits the lesson name + short description for the current
 * content language. Parent remounts with key={lessonId+lang} to reset drafts.
 */
export const LessonInfoPanel = ({
  courseId,
  lessonId,
  lessonNumber,
  name,
  description,
  lessonTheme,
  savePatch,
  isCourseAuthor,
  isTextLocked,
  isFrozen,
  onSave,
  onDelete,
}: Props) => {
  const m = useMessages(messages)
  // Подсказки полей — на языке правки: видно, что правится английская версия.
  const cm = useContentMessages(messages)
  const [tab, setTab] = useState<'info' | 'look'>('info')
  const { t, setT, d, setD, commit } = useLessonTextAutosave(name, description, onSave)

  const textDisabled = !isCourseAuthor || isTextLocked
  // Оформление урока уходит в заявку на проверку, но перевод их не
  // переписывает — поэтому свой флаг, а не `isTextLocked`.
  const assetDisabled = !isCourseAuthor || isFrozen

  return (
    // Поля — в своей прокрутке, «Удалить урок» — полосой под ней, прибито к
    // низу панели и видно всегда, сколько бы ни было текста.
    <div className="flex min-h-0 flex-1 flex-col">
      <ScrollArea className="flex min-h-0 flex-1 flex-col [&_[data-radix-scroll-area-viewport]>div]:!block [&_[data-radix-scroll-area-viewport]]:min-h-0 [&_[data-radix-scroll-area-viewport]]:flex-1">
        <div className="flex flex-col gap-4 p-4">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-foreground">
              {m.lessonNumber(lessonNumber)}
            </span>
          </div>

          <SegmentedControl fullWidth value={tab} onChange={setTab} options={tabsOf(m)} />

          {/* Обе вкладки смонтированы в ОДНОЙ ячейке сетки, неактивная — `invisible`:
          недописанное название не теряется при переключении, оформление не
          перечитывает блоки урока, а высота всегда по большей вкладке — шторка на
          телефоне не прыгает при переключении «Инфо» ⇄ «Оформление». */}
          <div className="grid [&>*]:[grid-area:1/1]">
            <div
              className={tab === 'info' ? 'flex flex-col gap-4' : 'invisible flex flex-col gap-4'}
            >
              <DisabledHint
                text={isTextLocked ? (isFrozen ? REVIEW_LOCK_TEXT : m.translationTextLocked) : null}
              >
                <div className="flex flex-col gap-4">
                  <div className="space-y-2">
                    <Label className="text-foreground font-medium">{m.lessonName}</Label>
                    <Input
                      value={t}
                      onChange={(e) => setT(e.target.value)}
                      onBlur={commit}
                      disabled={textDisabled}
                      // Пустое название — серой подсказкой «Урок N», как на плашке урока.
                      placeholder={cm.lessonNumber(lessonNumber)}
                      className="rounded-xl"
                    />
                  </div>

                  <LessonDescriptionField
                    value={d}
                    onChange={setD}
                    onBlur={commit}
                    disabled={textDisabled}
                  />
                </div>
              </DisabledHint>
            </div>

            {/* Картинки урока в студии нет: ни один плеер её не показывает. Поле
          `image` урока не трогаем — `buildLessonUpdate` шлёт его как есть. */}
            <div className={tab === 'look' ? undefined : 'invisible'}>
              <LessonLookSection
                courseId={courseId}
                lessonId={lessonId}
                lessonTheme={lessonTheme}
                savePatch={savePatch}
                disabled={assetDisabled}
                previewOnCanvas={tab === 'look'}
              />
            </div>
          </div>
        </div>
      </ScrollArea>
      {/* На «Оформлении» не видна, но место держит — та же причина: высота шторки. */}
      <div className={tab === 'info' ? 'shrink-0 p-4 pt-2' : 'invisible shrink-0 p-4 pt-2'}>
        <LessonDeleteButton
          lessonNumber={lessonNumber}
          isCourseAuthor={isCourseAuthor}
          isFrozen={isFrozen}
          onConfirm={onDelete}
        />
      </div>
    </div>
  )
}
